/*
 * /sea-route — расстояние и маршрут между портами ПО ВОДЕ.
 *
 * Всё считается в браузере: карта суши уже лежит в дереве (Natural Earth
 * 1:50 млн под /earth-globe), места берутся из общего справочника городов.
 * Ни одного запроса к чужому серверу, значит нет ни лимитов, ни очереди, ни
 * зависимости от чужой доступности — а поле здесь плотное и всё серверное
 * (sea-distances, searates, shiptraffic, oceanlook).
 *
 * Переделка 2026-09-02. Считал он и раньше верно (Роттердам — Сингапур 8301
 * миля против 8288 у sea-distances.org, 0.2 %), а показывал плохо: карта 400 px
 * в колонке 900 px, под ней таблица из 21 строки координат, и ради неё
 * приходилось листать мимо ответа. Теперь карта — главный предмет сцены,
 * числа стоят под ней, а список точек убран под «показать».
 *
 * Добавлено то, ради чего люди открывают ДВЕ вкладки конкурента:
 *   — время прихода по дате отхода, а не только «сколько суток»;
 *   — сравнение «через канал / вокруг» одним экраном: маршрут считается
 *     дважды и разница показывается сразу. Именно это решение и принимают,
 *     а у конкурентов оно требует двух прогонов и своей арифметики.
 */

import {
    COLS, ROWS, decodeRings, rasterize, carveCorridors, blockCorridors,
    snapToWater, lonToX, latToY,
} from './grid.js';
import { findRoute } from './route.js';
import { greatCircleM, rhumbBearing, NM } from '../maritime/geodesy.js';
import { makeT } from '../maritime/i18n.js';

const B = window.__SR || { T: {}, cfg: {} };
const T = B.T;
const t = makeT(T);
const $ = id => document.getElementById(id);
const el = (tag, cls, text) => { const n = document.createElement(tag); if (cls) n.className = cls; if (text != null) n.textContent = text; return n; };

const S = {
    grid: null, carved: null, ready: false,
    from: { lat: 51.95, lon: 4.14, name: 'Rotterdam' },
    to: { lat: 1.26, lon: 103.82, name: 'Singapore' },
    result: null,
    opts: { suez: true, panama: true, polar: false, speed: 14, fuel: 30, price: 600, dep: null },
    map: null, line: null, altLine: null, mFrom: null, mTo: null,
};

/* ── подготовка карты суши ───────────────────────────────────────────────── */
async function prepare() {
    setStatus(t('loading_land'));
    const url = (window.__SR_LAND || '/data/globe/countries-50m.json');
    const topo = await fetch(url, { cache: 'force-cache' }).then(r => r.json());
    setStatus(t('building_grid'));
    await new Promise(r => setTimeout(r, 0));
    const t0 = performance.now();
    S.grid = rasterize(decodeRings(topo, 'land'));
    S.carved = carveCorridors(S.grid);
    S.ready = true;
    setStatus('');
    window.__stage?.('grid_ready');
    return performance.now() - t0;
}

function setStatus(msg) {
    const b = $('srStatus');
    if (b) b.textContent = msg || '';
}

/* ── поиск места ─────────────────────────────────────────────────────────── */
/*
 * Через общий LocationInput, а не своим поиском по подстроке: он знает
 * нормализацию, «город N» / «пос. N» и RU-дополнение на 4 000 посёлков,
 * которое подгружается только после промаха. Своя копия этого не знает и
 * молча не находит половину российских портов.
 */
function wireSearch(inputId, listId, which) {
    const inp = $(inputId), list = $(listId);
    let deb;
    const close = () => { list.textContent = ''; list.hidden = true; };
    inp.addEventListener('input', () => {
        clearTimeout(deb);
        deb = setTimeout(async () => {
            const LI = window.LocationInput;
            if (!LI) return;
            let hits = [];
            try { await LI.loadData(); hits = await LI.searchWithFallback(inp.value, 8); }
            catch { return; }
            list.textContent = '';
            if (!hits.length) { close(); return; }
            for (const h of hits) {
                const c = LI.rowToCity(h.i);
                const b = el('button', 'sr-opt');
                b.type = 'button';
                // .sr-opt-n — устойчивая зацепка для стенда и для стилей: без
                // имени класса подсказку нельзя адресовать, а «первый span»
                // ломается от любой правки разметки.
                b.append(el('span', 'sr-opt-n', c.name), el('span', 'sr-opt-c', c.cc));
                b.addEventListener('click', () => {
                    S[which] = { lat: c.lat, lon: c.lng, name: c.name };
                    inp.value = c.name;
                    close();
                    updateMarkers();
                    compute();
                });
                list.append(b);
            }
            list.hidden = false;
        }, 200);
    });
    inp.addEventListener('blur', () => setTimeout(close, 180));
}

/* ── карта ───────────────────────────────────────────────────────────────── */
function initMap() {
    if (!window.L || S.map) return;
    S.map = L.map('srMap', { worldCopyJump: true, minZoom: 1 }).setView([30, 0], 2);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
        { maxZoom: 12, attribution: '© OpenStreetMap' }).addTo(S.map);
    const mk = (p, colour) => L.circleMarker([p.lat, p.lon], {
        radius: 7, color: colour, fillColor: colour, fillOpacity: 1, weight: 2,
    }).addTo(S.map);
    S.mFrom = mk(S.from, '#22c55e');
    S.mTo = mk(S.to, '#ef4444');
    let next = 'from';
    S.map.on('click', e => {
        const p = { lat: e.latlng.lat, lon: ((e.latlng.lng + 540) % 360) - 180, name: '' };
        S[next] = p;
        $(next === 'from' ? 'srFrom' : 'srTo').value = `${p.lat.toFixed(3)}, ${p.lon.toFixed(3)}`;
        next = next === 'from' ? 'to' : 'from';
        updateMarkers();
        compute();
    });
    // Карта живёт в сетке, которая меняет ширину при рефлоу консоли; без
    // invalidateSize Leaflet остаётся с прежними размерами и рисует полосы.
    // ⚠️ Safari до 13.1 наблюдателя не знает, и голый конструктор ронял boot()
    // в «Не удалось загрузить карту суши»: маршрута не было вовсе. Без него за
    // окном следит сам Leaflet (trackResize) — проверено замером.
    if (typeof ResizeObserver === 'function') new ResizeObserver(() => S.map && S.map.invalidateSize()).observe($('srMap'));
}

function updateMarkers() {
    if (!S.map) return;
    S.mFrom.setLatLng([S.from.lat, S.from.lon]);
    S.mTo.setLatLng([S.to.lat, S.to.lon]);
}

/* ── один прогон маршрута ────────────────────────────────────────────────── */
function runRoute(blocked, polar) {
    const a = snapToWater(S.grid, S.from.lat, S.from.lon);
    const b = snapToWater(S.grid, S.to.lat, S.to.lon);
    if (!a || !b) return { ok: false, reason: !a ? 'from_inland' : 'to_inland' };
    const undo = blockCorridors(S.grid, S.carved, blocked);
    const r = findRoute(S.grid, a, b, polar ? { maxLatN: 90, maxLatS: -90 } : {});
    undo();
    r.snapA = a; r.snapB = b;
    return r;
}

/* ── расчёт ──────────────────────────────────────────────────────────────── */
function compute() {
    if (!S.ready) return;
    const box = $('srOut');
    box.textContent = '';
    $('srCompare').textContent = '';
    $('srLegs').textContent = '';

    const blocked = [];
    if (!S.opts.suez) blocked.push('suez');
    if (!S.opts.panama) blocked.push('panama');

    const t0 = performance.now();
    const r = runRoute(blocked, S.opts.polar);
    const ms = performance.now() - t0;
    S.result = r;

    if (!r.ok) {
        const p = el('div', 'tc-msg bad', t('err_' + r.reason));
        box.append(p);
        if (S.line) { S.map.removeLayer(S.line); S.line = null; }
        if (S.altLine) { S.map.removeLayer(S.altLine); S.altLine = null; }
        return;
    }

    box.append(statsFor(r, ms));

    const a = r.snapA, b = r.snapB;
    if ((a.moved || 0) > 2 || (b.moved || 0) > 2) {
        box.append(el('div', 'tc-msg warn', t('snapped', { n: Math.max(a.moved, b.moved) * 7 })));
    }
    if (S.opts.polar && r.path.some(p => Math.abs(p.lat) > 70)) {
        box.append(el('div', 'tc-msg warn', t('polar_warning')));
    }

    drawRoute(r.path);
    renderCompare(r);
    renderLegs(r.path);
}

function metricsFor(r) {
    const hours = S.opts.speed > 0 ? r.distanceNm / S.opts.speed : 0;
    const fuel = hours * S.opts.fuel / 24;
    return { hours, fuel, cost: fuel * S.opts.price };
}

function statsFor(r, ms) {
    const wrap = el('div', 'tc-panel');
    const m = metricsFor(r);
    const direct = greatCircleM({ lat: S.from.lat, lon: S.from.lon }, { lat: S.to.lat, lon: S.to.lon }) / NM;
    const stats = el('div', 'tc-stats');
    const add = (l, v, sub, tone) => {
        const f = el('div', 'tc-stat' + (tone ? ' ' + tone : ''));
        f.append(el('div', 'tc-stat-l', l), el('div', 'tc-stat-v', v));
        if (sub) f.append(el('div', 'tc-stat-s', sub));
        stats.append(f);
    };
    add(t('f_dist'), Math.round(r.distanceNm) + ' ' + t('unit_nm'),
        t('f_dist_sub', { km: Math.round(r.distanceNm * 1.852) }), 'lead');
    add(t('f_time'), fmtDur(m.hours), t('f_time_sub', { n: S.opts.speed }));
    if (S.opts.dep) {
        const eta = new Date(S.opts.dep.getTime() + m.hours * 3600000);
        add(t('f_eta'), fmtDateTime(eta), t('f_eta_sub'));
    }
    add(t('f_fuel'), Math.round(m.fuel) + ' ' + t('unit_t'), t('f_fuel_sub', { n: S.opts.fuel }));
    add(t('f_cost'), Math.round(m.cost).toLocaleString(B.cfg.locale || 'ru') + ' $',
        t('f_cost_sub', { n: S.opts.price }));
    add(t('f_direct'), Math.round(direct) + ' ' + t('unit_nm'),
        t('f_direct_sub', { n: Math.round(r.distanceNm - direct) }));
    wrap.append(stats);
    wrap.append(el('p', 'tc-note', t('calc_note', { ms: Math.round(ms), pts: r.path.length })));
    return wrap;
}

/**
 * Сравнение «через канал» и «вокруг».
 *
 * Это и есть решение, ради которого открывают такой инструмент: стоит ли
 * платить за проход Суэцким каналом или идти вокруг Африки. У конкурентов
 * оно требует двух прогонов и своей арифметики; здесь второй прогон делает
 * инструмент и показывает разницу сразу. Считается только когда канал
 * действительно на пути — иначе это шум.
 */
function renderCompare(r) {
    const host = $('srCompare');
    host.textContent = '';
    const used = usedCorridors(r.path);
    if (!used.length) return;

    const rows = [];
    for (const c of used) {
        const blocked = [];
        if (!S.opts.suez) blocked.push('suez');
        if (!S.opts.panama) blocked.push('panama');
        if (!blocked.includes(c)) blocked.push(c);
        const alt = runRoute(blocked, S.opts.polar);
        if (!alt.ok) { rows.push({ c, alt: null }); continue; }
        rows.push({ c, alt, d: alt.distanceNm - r.distanceNm });
    }
    if (!rows.length) return;

    const p = el('div', 'tc-panel');
    p.append(el('div', 'tc-eyebrow', t('cmp_title')));
    const g = el('div', 'sr-cmp');
    for (const row of rows) {
        const label = row.c === 'suez' ? 'Suez' : 'Panama';
        g.append(el('span', null, t('cmp_via', { c: label })));
        g.append(el('b', null, Math.round(r.distanceNm) + ' ' + t('unit_nm')));
        if (!row.alt) {
            g.append(el('span', null, t('cmp_without', { c: label })));
            g.append(el('b', null, t('cmp_none')));
            continue;
        }
        const am = metricsFor(row.alt);
        const rm = metricsFor(r);
        g.append(el('span', null, t('cmp_without', { c: label })));
        g.append(el('b', null, Math.round(row.alt.distanceNm) + ' ' + t('unit_nm')));
        g.append(el('span', null, t('cmp_saves', { c: label })));
        g.append(el('b', null, `${Math.round(row.d)} ${t('unit_nm')} · ${fmtDur(am.hours - rm.hours)} · ${Math.round(am.cost - rm.cost).toLocaleString(B.cfg.locale || 'ru')} $`));
    }
    p.append(g);
    p.append(el('p', 'tc-note', t('cmp_longer')));
    host.append(p);
}

/** Через какие рукотворные проходы идёт маршрут. */
function usedCorridors(path) {
    const boxes = {
        suez: [29.0, 32.0, 31.5, 33.2],       // lat0, lon0, lat1, lon1
        panama: [7.0, -80.5, 10.0, -78.5],
    };
    const out = [];
    for (const [name, b] of Object.entries(boxes)) {
        if (path.some(p => p.lat >= b[0] && p.lat <= b[2] && p.lon >= b[1] && p.lon <= b[3])) out.push(name);
    }
    return out;
}

function fmtDur(hours) {
    if (!Number.isFinite(hours)) return '—';
    const sign = hours < 0 ? '−' : '';
    const abs = Math.abs(hours);
    const d = Math.floor(abs / 24), h = Math.round(abs % 24);
    return sign + (d ? t('dur_dh', { d, h }) : t('dur_h', { h: Math.round(abs) }));
}

function fmtDateTime(d) {
    try {
        return new Intl.DateTimeFormat(B.cfg.locale || 'ru', {
            day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false,
        }).format(d);
    } catch { return d.toISOString().slice(0, 16).replace('T', ' '); }
}

function drawRoute(path) {
    if (!S.map) return;
    if (S.line) S.map.removeLayer(S.line);
    // Разрываем линию на антимеридиане, иначе Leaflet протянет её через всю
    // карту обратно — маршрут выглядел бы идущим не туда.
    const parts = [[]];
    for (let i = 0; i < path.length; i++) {
        if (i && Math.abs(path[i].lon - path[i - 1].lon) > 180) parts.push([]);
        parts[parts.length - 1].push([path[i].lat, path[i].lon]);
    }
    S.line = L.polyline(parts.filter(p => p.length > 1), { color: '#4aa3ff', weight: 3.5 }).addTo(S.map);
    try { S.map.fitBounds(S.line.getBounds(), { padding: [26, 26], maxZoom: 6 }); } catch { /* пустой маршрут */ }
}

/*
 * Список точек — под «показать». Он нужен тому, кто переносит маршрут в
 * прокладчик, и мешает всем остальным: двадцать одна строка координат стояла
 * между ответом и кнопкой выгрузки.
 */
function renderLegs(path) {
    const host = $('srLegs');
    host.textContent = '';
    if (path.length < 2) return;
    const d = el('details', 'tc-panel sr-legs');
    const sum = el('summary', null, t('legs_title', { n: path.length }));
    d.append(sum);
    const wrap = el('div', 'tc-tablewrap');
    const tbl = el('table', 'tc-table');
    const thead = el('thead'), hr = el('tr');
    for (const k of ['leg_n', 'leg_pos', 'leg_course', 'leg_dist']) hr.append(el('th', null, t(k)));
    thead.append(hr); tbl.append(thead);
    const body = el('tbody');
    for (let i = 0; i + 1 < path.length; i++) {
        const a = path[i], b = path[i + 1];
        const tr = el('tr');
        tr.append(el('td', null, String(i + 1)));
        tr.append(el('td', 'sr-mono', `${fmtLat(a.lat)} ${fmtLon(a.lon)}`));
        tr.append(el('td', 'num', rhumbBearing(a, b).toFixed(0).padStart(3, '0') + '°'));
        tr.append(el('td', 'num', (greatCircleM(a, b) / NM).toFixed(0)));
        body.append(tr);
    }
    const last = path[path.length - 1];
    const tr = el('tr');
    tr.append(el('td', null, String(path.length)));
    tr.append(el('td', 'sr-mono', `${fmtLat(last.lat)} ${fmtLon(last.lon)}`));
    tr.append(el('td', null, ''), el('td', null, ''));
    body.append(tr);
    tbl.append(body); wrap.append(tbl); d.append(wrap);
    host.append(d);
}

const fmtLat = v => `${Math.abs(v).toFixed(2)}°${v >= 0 ? 'N' : 'S'}`;
const fmtLon = v => `${Math.abs(v).toFixed(2)}°${v >= 0 ? 'E' : 'W'}`;

/* ── GPX ─────────────────────────────────────────────────────────────────── */
function exportGpx() {
    if (!S.result?.ok) return;
    const esc = s => String(s).replace(/[<>&"']/g, c => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&apos;' }[c]));
    const name = `${S.from.name || 'from'} - ${S.to.name || 'to'}`;
    const pts = S.result.path.map((p, i) =>
        `    <rtept lat="${p.lat.toFixed(6)}" lon="${p.lon.toFixed(6)}"><name>WP${i + 1}</name></rtept>`).join('\n');
    const gpx = `<?xml version="1.0" encoding="UTF-8"?>
<gpx version="1.1" creator="Timbrica" xmlns="http://www.topografix.com/GPX/1/1">
  <rte><name>${esc(name)}</name>
${pts}
  </rte>
</gpx>
`;
    const blob = new Blob([gpx], { type: 'application/gpx+xml' });
    const file = (name.replace(/[^\w\s.-]+/g, '_') || 'route') + '.gpx';
    window.__downloadFile?.(blob, file);
    window.__aeFunnel?.('gpx_export', { nm: Math.round(S.result.distanceNm) });
}

/* ── подключение ─────────────────────────────────────────────────────────── */
function bind() {
    wireSearch('srFrom', 'srFromList', 'from');
    wireSearch('srTo', 'srToList', 'to');
    $('srSwap').addEventListener('click', () => {
        const a = S.from; S.from = S.to; S.to = a;
        const f = $('srFrom'), tt = $('srTo');
        const v = f.value; f.value = tt.value; tt.value = v;
        updateMarkers(); compute();
    });
    for (const [id, key] of [['srSuez', 'suez'], ['srPanama', 'panama'], ['srPolar', 'polar']]) {
        $(id).addEventListener('change', e => { S.opts[key] = e.target.checked; compute(); });
    }
    for (const [id, key] of [['srSpeed', 'speed'], ['srFuel', 'fuel'], ['srPrice', 'price']]) {
        $(id).addEventListener('input', e => {
            S.opts[key] = Number(e.target.value) || 0;
            clearTimeout(bind._d); bind._d = setTimeout(compute, 150);
        });
        window.__toolSettings?.bind('sea-route', $(id), key, $(id).value);
    }
    const dep = $('srDep');
    dep.addEventListener('input', () => {
        S.opts.dep = dep.value ? new Date(dep.value) : null;
        compute();
    });
    $('srExport').addEventListener('click', exportGpx);
    $('srPrint').addEventListener('click', () => window.print());
}

async function boot() {
    try {
        bind();
        initMap();
        $('srFrom').value = 'Rotterdam';
        $('srTo').value = 'Singapore';
        const ms = await prepare();
        setStatus(t('grid_built', { ms: Math.round(ms), cols: COLS, rows: ROWS }));
        updateMarkers();
        compute();
        window.__stage?.('ready');
    } catch (err) {
        window.__handleError?.(err, 'sea-route');
        setStatus(t('load_error'));
    }
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();
